<template>
  <section>
    <b-steps v-model="activeStep" :has-navigation="false" id="insurance-steps">
      <b-step-item label="So funktioniert's" :clickable="false" class="mt-5">
        <div class="content">
          <h2 class="title is-size-5">{{ $t('insurance.form.insurance.workflow') }}</h2>
          <p>{{ $t('insurance.form.insurance.necessary-data') }}</p>

          <p>{{ $t('insurance.form.insurance.get_policy') }}</p>
        </div>

        <div class="level mt-5 is-mobile">
          <div class="level-left"></div>
          <div class="level-right">
            <div class="level-item">
              <b-button type="is-primary" name="dataButton" @click="nextStep" icon-right="chevron-right">{{
                $t('insurance.form.insurance.input') }}</b-button>
            </div>
          </div>
        </div>
      </b-step-item>

      <b-step-item label="Daten" :clickable="false" class="mt-5">
        <h2 class="title is-size-5">{{ $t('general.data') }}</h2>
        <p class="mb-5">{{ $t('insurance.form.insurance.travellers-data') }}</p>

        <ValidationObserver tag="form" ref="observer" v-slot="{ invalid }">
          <div class="columns">
            <div class="column is-6" v-for="customer in customers" :key="customer.index">
              <h3 class="title is-size-6">{{ $t('insurance.form.insurance.traveller') }} {{ customer.index }}</h3>
              <b-field>
                <ValidationProvider name="Herr/Frau" rules="oneOf:10,20" v-slot="{ errors }">
                  <b-radio :id="`radioMale_${customer.index}`" v-model="customer.idTitle"
                    :name="`idTitle_${customer.index}`" native-value="10">{{ $t('general.male') }}</b-radio>
                  <b-radio :id="`radioFemale_${customer.index}`" v-model="customer.idTitle"
                    :name="`idTitle_${customer.index}`" native-value="20">{{ $t('general.female') }}</b-radio>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </ValidationProvider>
              </b-field>

              <ValidationProvider name="Vorname" :rules="{ required: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.firstname')}*`" expanded label-position="inside">
                  <b-input :name="`firstName__${customer.index}`" v-model="customer.firstName"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="Nachname" :rules="{ required: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.lastname')}*`" expanded label-position="inside">
                  <b-input :name="`lastName_${customer.index}`" v-model="customer.lastName"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="Straße" :rules="{ required: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.street')}*`" expanded label-position="inside">
                  <b-input :name="`streetInput_${customer.index}`" v-model="customer.street"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="Hausnummer" :rules="{ required: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.number')}*`" expanded label-position="inside">
                  <b-input :name="`streetNumberInput_${customer.index}`" v-model="customer.streetNumber"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="PLZ" :rules="{ required: true, numeric: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.zip')}*`" expanded label-position="inside">
                  <b-input :name="`plz_${customer.index}`" v-model="customer.postalCode"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="Ort" :rules="{ required: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.city')}*`" expanded label-position="inside">
                  <b-input :name="`city_${customer.index}`" v-model="customer.city"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="Land" :rules="{ required: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.country')}*`" label-position="inside">
                  <b-select placeholder="Land" v-model="customer.country" icon="globe" expanded>
                    <option v-for="option in isoCountries" :value="option.iso" :key="option.iso">{{ option.country }}
                    </option>
                  </b-select>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="E-Mail" :rules="{ required: true, email: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.email')}*`" expanded label-position="inside">
                  <b-input :name="`mail_${customer.index}`" v-model="customer.email" inputmode="email"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
              <ValidationProvider name="Geburtsdatum" :rules="{ required: true, date: true }" v-slot="{ errors }">
                <b-field :label="`${$t('general.birthday')}*`" expanded label-position="inside">
                  <b-input :name="`birthday_${customer.index}`" v-model="customer.birthday"></b-input>
                  <p class="help is-danger" v-for="error in errors" :key="error">{{ error }}</p>
                </b-field>
              </ValidationProvider>
            </div>
          </div>
          <div class="level mt-5 is-mobile">
            <div class="level-left">
              <div class="level-item">
                <b-button @click="activeStep--" icon-left="chevron-left">{{ $t('insurance.form.insurance.info')
                }}</b-button>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <b-button type="is-primary" name="overviewButton" :disabled="invalid" @click="nextStep"
                  icon-right="chevron-right">{{ $t('insurance.form.insurance.closure') }}</b-button>
              </div>
            </div>
          </div>
        </ValidationObserver>

        <small>(* {{ $t('general.required-field') }})</small>
      </b-step-item>

      <b-step-item label="Abschluss" :clickable="false" class="mt-5">
        <div class="content">
          <h2 class="title is-size-5">{{ $t('insurance.form.insurance.insurance-closure') }} {{ id }}</h2>
          <p>{{ $t('insurance.form.insurance.check_data') }}</p>
        </div>

        <div class="columns is-multiline">
          <div class="column is-6" v-for="customer in customers" :key="customer.index">
            <h4 class="title is-size-6 is-marginless">{{ $t('insurance.form.insurance.traveller') }} {{ customer.index }}
            </h4>
            <p v-if="customer">
              {{ customer.idTitle == 10 ? $t('general.male') : $t('general.female') }}<br />
              {{ customer.firstName }} {{ customer.lastName }}<br />
              {{ customer.street }} {{ customer.streetNumber }}<br />
              {{ customer.postalCode }} {{ customer.city }}<br />
              {{ customer.country }}<br />
              {{ $t('general.email') }}: {{ customer.email }}<br />
              {{ $t('general.birthday') }}: {{ customer.birthday }}
            </p>
          </div>
        </div>
        <b-button type="is-light" @click="activeStep--">{{ $t('general.edit-data') }}</b-button>

        <Separator />
        <div class="notification is-danger" v-show="hasErrors">
          <span v-for="error in insuranceErrors" :key="error">{{ error }}</span>
        </div>
        <div class="level mt-5 is-mobile">
          <div class="level-left">
            <div class="level-item">
              <b-button @click="activeStep--" icon-left="chevron-left">{{ $t('general.data') }}</b-button>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <b-button type="is-primary" name="overviewButton" @click="sendInsuranceData" icon-right="chevron-right">{{
                $t('general.submit') }}</b-button>
            </div>
          </div>
        </div>

        <p class="is-size-7">
          {{ $t('insurance.form.insurance.agreement') }}
        </p>
      </b-step-item>
    </b-steps>
  </section>
</template>

<script>
import Separator from '@/components/Separator'
import { mapGetters } from 'vuex'
import InsuranceApi from '@/api/insurance-api'

export default {
  name: 'Insurance',
  components: {
    Separator,
  },
  props: {
    upgrade: Object,
    token: String,
    orderId: String,
    id: Number,
  },
  computed: {
    ...mapGetters({
      isoCountries: 'isoCountries',
    }),
    hasErrors() {
      return this.insuranceErrors.length > 0
    },
  },
  data() {
    return {
      activeStep: 0,
      isCustomerDataValid: false,
      customers: [],
      insuranceErrors: [],
    }
  },
  watch: {
    upgrade: {
      immediate: true,
      handler(newUpgrade) {
        if (newUpgrade) {
          this.customers = []
          if (!newUpgrade.persons && newUpgrade.unitPrice == 0) {
            for (let i = 1; i <= 2; i++) {
              this.customers.push({ index: i, idTitle: 10, firstName: null, lastName: null, birthday: null, email: null, street: null, streetNumber: null, postalCode: null, city: null, country: null })
            }
          } else {
            for (let i = 1; i <= newUpgrade.persons; i++) {
              this.customers.push({ index: i, idTitle: 10, firstName: null, lastName: null, birthday: null, email: null, street: null, streetNumber: null, postalCode: null, city: null, country: null })
            }
          }

        }
      },
    },
  },
  methods: {
    async sendInsuranceData() {
      //send data
      try {
        let result = await InsuranceApi.bookInsurance(this.orderId, this.token, this.id, this.customers)
        if (result.success) {
          this.$emit('data-sent', this.id)
        } else {
          this.insuranceErrors = result.errors
        }
      } catch {
        this.insuranceErrors = [this.$t('errors.generic-error-check-input')]
      }
    },
    nextStep() {
      this.activeStep++
      // this.$scrollTo('#insurance-steps', 500, {
      //   offset: -160,
      // })
    },
  },
  async mounted() { },
}
</script>

<style lang="scss"></style>
